import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useLocalizeMessage, ModalWindow, Spinner, useToastNotification } from 'libs.nucleus';
import { EM_DASH } from 'libs.react';
import { PasswordReauthModal } from 'libs.react_internal';
import { PIOversightDetails } from './components/pi_oversight_details.component';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { usePIOversight } from '../../hooks/use_pi_oversight';
import { usePIOversightSignoff } from '../../hooks/use_pi_oversight_signoff';
export const PiOversightDetailsModal = ({ isOpen, participantId, onClose, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const account = useAxonAccountContext();
    const { participantData, reviewBundle, fetchReviewBundleRequestState, fetchSummaryRequestState, fetchParticipantRequestState, getActivityTaskResponses, } = usePIOversight({
        participantId,
    });
    const { isProcessingSignoff, triggerSignOff, showPasswordModal, closePasswordModal, handlePasswordSubmit, passwordError, } = usePIOversightSignoff(participantId, {
        onSuccess: () => {
            addNotification({
                title: '',
                subtitle: translate('label.pi_oversight.signoff_success'),
                type: 'success',
            });
            onClose();
        },
        onError: (errorMessage) => {
            addNotification({
                title: '',
                subtitle: errorMessage,
                type: 'error',
            });
        },
        defaultPrintedName: account?.name ? `${account.name.first} ${account.name.last}` : '',
    });
    const handleSignBtnClick = async () => {
        try {
            // Trigger re-authentication (shows password modal or redirects to SSO)
            await triggerSignOff();
        }
        catch (error) {
            addNotification({
                title: '',
                subtitle: translate(error.message) || translate('error.pi_oversight.start_authentication_failed_message'),
                type: 'error',
            });
        }
    };
    const reviewParticipantNumberTitle = `${translate('label.pi_oversight.review')} ${participantData?.c_number ?? EM_DASH}`;
    const modalConfig = useMemo(() => {
        const isDataReady = !!reviewBundle?.serverHash;
        const hasNoReviewableData = !reviewBundle?.responses?.length && !reviewBundle?.dcrItems?.length && !reviewBundle?.visitEvents?.length;
        return {
            title: reviewParticipantNumberTitle,
            footerSecondaryActionButtons: [
                {
                    label: translate('generic.cancel'),
                    onClick: onClose,
                    disabled: isProcessingSignoff,
                },
            ],
            footerPrimaryActionButton: {
                label: isProcessingSignoff ? translate('action.pi_oversight.signing') : translate('action.pi_oversight.sign'),
                onClick: handleSignBtnClick,
                variant: 'primary',
                disabled: !isDataReady || hasNoReviewableData || isProcessingSignoff,
            },
        };
    }, [
        participantData,
        translate,
        reviewBundle,
        fetchSummaryRequestState,
        isProcessingSignoff,
        onClose,
        handleSignBtnClick,
    ]);
    const showInitialLoader = fetchReviewBundleRequestState?.isLoading || fetchParticipantRequestState?.isLoading;
    const hasError = fetchReviewBundleRequestState?.isError || fetchParticipantRequestState?.isError;
    return (_jsxs(_Fragment, { children: [_jsx(ModalWindow, { title: modalConfig.title, isOpen: isOpen, closeWindow: onClose, footerSecondaryActionButtons: modalConfig.footerSecondaryActionButtons, footerPrimaryActionButton: modalConfig.footerPrimaryActionButton, width: 'full', removeBodyPadding: true, dataTestId: 'pi-oversight-details-modal', children: showInitialLoader ? (_jsx("div", { className: 'p-6', children: _jsx(Spinner, { wrapper: 'full' }) })) : hasError ? (_jsx("div", { className: 'p-6 text-center', children: _jsx("p", { className: 'text-red-600', children: translate('error.pi_oversight.load_review_data') }) })) : (_jsx(PIOversightDetails, { reviewBundle: reviewBundle, getActivityTaskResponses: getActivityTaskResponses })) }), showPasswordModal && (_jsx(PasswordReauthModal, { isOpen: showPasswordModal, email: account?.email || '', onSubmit: handlePasswordSubmit, onCancel: closePasswordModal, isLoading: isProcessingSignoff, error: passwordError, defaultPrintedName: account?.name ? `${account.name.first} ${account.name.last}` : '', title: reviewParticipantNumberTitle, heading: translate('label.pi_oversight.sign_confirmation_heading'), subheading: translate('label.pi_oversight.sign_confirmation') }))] }));
};
